"use client";

import { useState } from "react";
import Image from "next/image";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import type { VideoTestimonial } from "@/lib/videoTestimonials";
import { MarketingIconBadge } from "@/components/marketing/MarketingIconBadge";

type VideoTestimonialCardsProps = {
  items: VideoTestimonial[];
  columns?: 1 | 2 | 3;
  compact?: boolean;
  featuredId?: VideoTestimonial["id"];
  presentation?: "cards" | "aliProof" | "watchable";
  eventPrefix?: string;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
};

function buildGridClassName(columns: 1 | 2 | 3, itemCount: number) {
  if (columns === 1) return "grid gap-4";
  if (columns === 2 && itemCount === 3) return "grid gap-4 md:grid-cols-2 xl:grid-cols-3";
  if (columns === 2) return "grid gap-4 md:grid-cols-2";
  return "grid gap-4 md:grid-cols-2 xl:grid-cols-3";
}

function WatchableVideoTestimonialCard({
  item,
  eventName,
  eventProperties,
}: {
  item: VideoTestimonial;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const trackEvent = useTrackEvent();

  const activateVideo = () => {
    setIsPlaying(true);
    if (eventName) {
      trackEvent(eventName, {
        testimonial_id: item.id,
        testimonial_person: item.person,
        testimonial_title: item.title,
        ...eventProperties,
      });
    }
  };

  const trackYoutubeClick = () => {
    trackEvent("testimonial_youtube_click", {
      testimonial_id: item.id,
      testimonial_person: item.person,
      testimonial_title: item.title,
      destination_url: item.youtubeUrl,
      ...eventProperties,
    });
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-[20px] border border-[#ddcfb7] bg-[#fffaf2] shadow-[0_18px_42px_rgba(11,45,39,0.12)] ring-1 ring-white/70">
      <div className="overflow-hidden bg-black">
        {isPlaying ? (
          <div className="aspect-video w-full">
            <iframe
              className="h-full w-full"
              src={`${item.embedUrl}&autoplay=1`}
              title={`${item.person} testimonial video`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={activateVideo}
            className="group relative block aspect-video w-full overflow-hidden bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#f2c963]"
            data-amp-href={item.embedUrl}
            aria-label={`Play video testimonial from ${item.person}`}
          >
            <Image
              src={item.posterSrc}
              alt={`${item.person} video testimonial preview`}
              fill
              quality={90}
              sizes="(min-width: 1280px) 28vw, (min-width: 768px) 46vw, 92vw"
              className="object-cover transition duration-500 group-hover:scale-[1.025]"
            />
            <span className="absolute inset-0 bg-black/10 transition group-hover:bg-black/[0.16]" aria-hidden="true" />
            <span
              className="absolute left-1/2 top-1/2 inline-flex h-[4.5rem] w-[4.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white/85 bg-[#d5a746] text-[#063d35] shadow-[0_14px_34px_rgba(0,0,0,0.34)] transition group-hover:scale-105 group-hover:bg-[#e3b654]"
              aria-hidden="true"
            >
              <span className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-[#063d35]" />
            </span>
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.22em] text-[#a66f16]">Video testimonial</p>
          <p className="text-xs font-bold text-[#68736e]">{item.person}</p>
        </div>
        <h3 className="mt-2 font-serif text-[1.45rem] font-semibold leading-[1.12] tracking-tight text-[#123b34]">
          {item.title}
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-6 text-[#52615c]">{item.summary}</p>

        <div className="mt-auto grid gap-2 pt-5 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          {isPlaying ? (
            <span className="inline-flex min-h-11 w-full items-center justify-center rounded-[12px] bg-[#e9dfca] px-4 text-sm font-black text-[#123b34] sm:flex-1" aria-live="polite">
              Video playing above
            </span>
          ) : (
            <button
              type="button"
              onClick={activateVideo}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-[#d5a746] px-4 text-sm font-black text-[#102f2a] shadow-[0_12px_26px_rgba(155,107,25,0.18)] transition hover:-translate-y-0.5 hover:bg-[#e0b95b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#946516] sm:flex-1"
            >
              <MarketingIconBadge icon="play" variant="light" className="h-7 w-7 border-0 bg-transparent text-[#123b34] shadow-none" iconClassName="h-4 w-4" />
              Watch {item.person}&apos;s video
            </button>
          )}
          <a
            href={item.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackYoutubeClick}
            className="inline-flex min-h-10 items-center justify-center px-1 text-xs font-black uppercase tracking-[0.16em] text-[#7a5114] transition hover:text-[#123b34]"
          >
            YouTube ↗
          </a>
        </div>
      </div>
    </article>
  );
}

function VideoTestimonialCard({
  item,
  compact = false,
  eventName,
  eventProperties,
  featured = false,
}: {
  item: VideoTestimonial;
  compact?: boolean;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  featured?: boolean;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const trackEvent = useTrackEvent();

  const activateVideo = () => {
    setIsPlaying(true);
    if (eventName) {
      trackEvent(eventName, {
        testimonial_id: item.id,
        testimonial_person: item.person,
        testimonial_title: item.title,
        ...eventProperties,
      });
    }
  };

  const trackYoutubeClick = () => {
    trackEvent("testimonial_youtube_click", {
      testimonial_id: item.id,
      testimonial_person: item.person,
      testimonial_title: item.title,
      destination_url: item.youtubeUrl,
      ...eventProperties,
    });
  };

  return (
    <article
      className={`overflow-hidden rounded-[28px] border bg-surface/76 p-4 shadow-[0_18px_52px_rgba(16,18,28,0.14)] dark:shadow-[0_24px_70px_rgba(2,4,13,0.34)] ${
        featured
          ? "border-emerald/30 ring-1 ring-emerald/15 md:col-span-2 xl:col-span-2"
          : "border-border-subtle"
      }`}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.28em] text-emerald/80">
              {featured ? "Featured testimonial" : item.contextLabel}
            </p>
            <h3 className={`${compact && !featured ? "text-xl" : "text-2xl"} font-serif font-semibold text-foreground`}>
              {item.title}
            </h3>
          </div>
          <span className="rounded-full border border-border-strong px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-text-muted">
            {item.person}
          </span>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-border-subtle bg-black shadow-[0_16px_46px_rgba(16,18,28,0.18)]">
          {isPlaying ? (
            <div className="aspect-video w-full">
              <iframe
                className="h-full w-full"
                src={`${item.embedUrl}&autoplay=1`}
                title={`${item.person} testimonial video`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={activateVideo}
              className="group relative block aspect-video w-full overflow-hidden bg-black text-left"
              data-amp-href={item.embedUrl}
              aria-label={`Play testimonial from ${item.person}`}
            >
              <Image
                src={item.posterSrc}
                alt={`${item.person} testimonial preview`}
                fill
                quality={90}
                sizes={
                  featured
                    ? "(min-width: 1280px) 58vw, (min-width: 768px) 94vw, 92vw"
                    : "(min-width: 1536px) 29vw, (min-width: 1280px) 31vw, (min-width: 768px) 47vw, 92vw"
                }
                className="object-cover transition duration-300 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,8,15,0.05),rgba(5,8,15,0.52))]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-white/12 text-2xl text-white shadow-[0_10px_30px_rgba(2,4,13,0.45)] backdrop-blur-md transition group-hover:bg-white/18">
                  ▶
                </span>
              </div>
              <div className="absolute inset-x-4 bottom-4 rounded-[20px] border border-white/10 bg-[rgba(17,24,39,0.76)] px-4 py-3 shadow-[0_14px_34px_rgba(2,4,13,0.28)] backdrop-blur-md">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#d9a22c]/45 bg-[#fffaf2]/12 text-[#e2b43e]">
                    <MarketingIconBadge icon="play" variant="dark" className="h-7 w-7 border-0 bg-transparent text-[#e2b43e] shadow-none" iconClassName="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-[#e2b43e]">{item.role}</span>
                    <span className="mt-1 block text-sm font-semibold leading-tight text-white">{item.title}</span>
                    <span className="mt-1 block text-xs font-medium text-white/78">{item.person}</span>
                  </span>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-sm leading-relaxed text-text-secondary">
            {item.summary}
          </p>
          <a
            href={item.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackYoutubeClick}
            className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.24em] text-emerald transition hover:text-emerald/80"
          >
            Watch on YouTube →
          </a>
        </div>
      </div>
    </article>
  );
}

function AliProofVideoCard({
  item,
  eventName,
  eventProperties,
}: {
  item: VideoTestimonial;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const trackEvent = useTrackEvent();

  const activateVideo = () => {
    setIsPlaying(true);
    if (eventName) {
      trackEvent(eventName, {
        testimonial_id: item.id,
        testimonial_person: item.person,
        testimonial_title: item.title,
        ...eventProperties,
      });
    }
  };

  const trackYoutubeClick = () => {
    trackEvent("testimonial_youtube_click", {
      testimonial_id: item.id,
      testimonial_person: item.person,
      testimonial_title: item.title,
      destination_url: item.youtubeUrl,
      ...eventProperties,
    });
  };

  return (
    <article className="overflow-hidden rounded-[24px] border border-[#e4dccd] bg-white/88 p-3.5 shadow-[0_20px_58px_rgba(46,61,51,0.08)] ring-1 ring-white/70 sm:p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="inline-flex rounded-full bg-[#f6efe3] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#a66f16]">
            {item.contextLabel}
          </p>
          <h3 className="mt-1.5 font-serif text-2xl font-semibold leading-tight tracking-tight text-[#123b34] sm:text-[1.7rem]">
            {item.title}
          </h3>
        </div>
        <div className="inline-flex items-center gap-2.5 rounded-full border border-[#d7cec0] bg-white px-3 py-1.5 shadow-[0_8px_22px_rgba(46,61,51,0.06)]">
          <span className="inline-flex h-7 w-10 items-center justify-center rounded-[8px] bg-[#e82418] text-white shadow-sm" aria-hidden="true">
            <span className="ml-0.5 h-0 w-0 border-y-[5px] border-l-[8px] border-y-transparent border-l-white" />
          </span>
          <span className="text-sm leading-tight text-[#151719]">
            <span className="block font-semibold uppercase tracking-[0.04em]">{item.person}</span>
            <span className="block text-[#151719]/74">YouTube creator</span>
          </span>
        </div>
      </div>

      <div className="overflow-hidden rounded-[18px] border border-[#e4dccd] bg-black shadow-[0_16px_40px_rgba(18,33,31,0.14)]">
        {isPlaying ? (
          <div className="aspect-video w-full">
            <iframe
              className="h-full w-full"
              src={`${item.embedUrl}&autoplay=1`}
              title={`${item.person} testimonial video`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={activateVideo}
            className="group relative block aspect-video w-full overflow-hidden bg-black text-left"
            data-amp-href={item.embedUrl}
            aria-label={`Play testimonial from ${item.person}`}
          >
            <Image
              src={item.posterSrc}
              alt={`${item.person} testimonial preview`}
              fill
              quality={90}
              sizes="(min-width: 1280px) 50vw, (min-width: 768px) 86vw, 92vw"
              className="object-cover transition duration-300 group-hover:scale-[1.02]"
            />
            <span className="absolute left-1/2 top-1/2 inline-flex h-[4.55rem] w-[5.15rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[19px] bg-[#f12b21] text-white transition group-hover:scale-[1.04] sm:h-[5.5rem] sm:w-[6.25rem] sm:rounded-[22px]" aria-hidden="true">
              <span className="ml-1 h-0 w-0 border-y-[11px] border-l-[18px] border-y-transparent border-l-white sm:border-y-[13px] sm:border-l-[21px]" />
            </span>
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-4">
        <blockquote className="grid grid-cols-[1.65rem_minmax(0,1fr)] gap-2.5 text-base leading-relaxed text-[#151719]/82">
          <span className="font-serif text-3xl leading-none text-[#bd8b25]" aria-hidden="true">&ldquo;</span>
          <span>{item.summary}</span>
        </blockquote>
        <div className="grid gap-3 border-t border-[#e4dccd] pt-3 sm:grid-cols-3">
          {[
            { icon: "people" as const, title: "Trusted creator", copy: "Established public voice" },
            { icon: "check-circle" as const, title: "Verified", copy: "Approved student story" },
            { icon: "shield" as const, title: "Trusted", copy: "High standards of coaching" },
          ].map((proof) => (
            <div key={proof.title} className="grid grid-cols-[2.15rem_minmax(0,1fr)] items-center gap-2.5">
              <MarketingIconBadge icon={proof.icon} variant="light" className="h-9 w-9 border-0 bg-transparent text-[#123b34] shadow-none" iconClassName="h-6 w-6" />
              <p className="text-xs leading-tight text-[#151719]/72">
                <span className="block font-semibold text-[#151719]">{proof.title}</span>
                {proof.copy}
              </p>
            </div>
          ))}
        </div>
        <a
          href={item.youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackYoutubeClick}
          className="inline-flex min-h-12 items-center justify-between gap-4 rounded-[13px] bg-[#0c4038] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#f2c963] shadow-[0_14px_30px_rgba(18,33,31,0.16)] transition hover:bg-[#123b34] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d9a22c]"
        >
          <span className="inline-flex items-center gap-3">
            <MarketingIconBadge icon="play" variant="dark" className="h-7 w-7 border-[#d9a22c] bg-transparent text-[#d9a22c] shadow-none" iconClassName="h-4 w-4" />
            Watch Ali&apos;s full story on YouTube
          </span>
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </article>
  );
}

export function VideoTestimonialCards({
  items,
  columns = 2,
  compact = false,
  featuredId,
  presentation = "cards",
  eventPrefix = "video_testimonial",
  eventName,
  eventProperties,
}: VideoTestimonialCardsProps) {
  if (presentation === "aliProof") {
    const item = items[0];
    if (!item) return null;

    return (
      <AliProofVideoCard
        item={item}
        eventName={eventName ?? `${eventPrefix}_${item.id}_play`}
        eventProperties={eventProperties}
      />
    );
  }

  const orderedItems = featuredId
    ? [...items].sort((a, b) => {
        if (a.id === featuredId) return -1;
        if (b.id === featuredId) return 1;
        return 0;
      })
    : items;

  if (presentation === "watchable") {
    return (
      <div className={buildGridClassName(columns, orderedItems.length)}>
        {orderedItems.map((item) => (
          <WatchableVideoTestimonialCard
            key={item.id}
            item={item}
            eventName={eventName ?? `${eventPrefix}_${item.id}_play`}
            eventProperties={eventProperties}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={buildGridClassName(columns, orderedItems.length)}>
      {orderedItems.map((item) => (
        <VideoTestimonialCard
          key={item.id}
          item={item}
          compact={compact}
          featured={item.id === featuredId}
          eventName={eventName ?? `${eventPrefix}_${item.id}_play`}
          eventProperties={eventProperties}
        />
      ))}
    </div>
  );
}
